/**
 * Reading the consent mirror cookie, on either side. Not in `usercentrics.ts`, which is browser-only and
 * 500s SSR when a `.server.ts` file imports it; writing stays there, since it reads the CMP's storage.
 */

export interface UsercentricsDecision {
	readonly granted: readonly string[];
	readonly denied: readonly string[];
}

/**
 * SSR cannot see the CMP's `localStorage`, so without this every gated embed renders "not decided" and
 * the client visibly corrects it. A mirror only: a stale value self-heals (`syncConsentCookie`).
 */
export const CONSENT_COOKIE_NAME = 'wc-consent';

/** Bump with the payload shape; another version reads as no cookie. */
export const CONSENT_COOKIE_VERSION = 1;

/** Short, so the mirror cannot much outlive the consent if the client never refreshes it. */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

/** `undefined` means "no answer yet", never a denial. */
export const parseConsentCookie = (value: string | undefined | null): UsercentricsDecision | undefined => {
	if (!value) return undefined;
	try {
		const parsed: unknown = JSON.parse(decodeURIComponent(value));
		if (typeof parsed !== 'object' || parsed === null) return undefined;
		if (!('v' in parsed) || parsed.v !== CONSENT_COOKIE_VERSION) return undefined;
		if (!('g' in parsed) || !('d' in parsed)) return undefined;
		const { g, d } = parsed;
		if (!Array.isArray(g) || !Array.isArray(d)) return undefined;
		const granted = g.filter((name): name is string => typeof name === 'string');
		const denied = d.filter((name): name is string => typeof name === 'string');
		if (granted.length === 0 && denied.length === 0) return undefined;
		return { granted, denied };
	} catch {
		return undefined;
	}
};

/** The same string the server reads with `getCookie`, so the first client render matches its markup. */
export const readConsentCookie = (): UsercentricsDecision | undefined => {
	if (typeof document === 'undefined') return undefined;
	const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${CONSENT_COOKIE_NAME}=`));
	return parseConsentCookie(match?.slice(CONSENT_COOKIE_NAME.length + 1));
};
